import { getUserInputFormFieldKey } from 'src/modules/common/hooks/getUserInputFormFieldKey.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { UserInput } from 'src/modules/user-input/types.ts';
import { ValveProduct } from 'src/modules/valves/ValvesPageMapping.ts';
import { RegionsEnum } from 'src/generated/operations.ts';

type Props = {
    userInputState: UserInput;
    valve: ValveProduct;
    region: RegionsEnum;
}

export type DesignRow = { key:string; detectNamespace?: boolean} | string;

const createItem = (
    name: string | null | undefined,
    value: string | null | undefined,
    sequence?: string | null | undefined,
): [string | null | undefined, string | null | undefined, string | null | undefined] => [name, value, sequence];

export const buildDesignDataContentRaw = ({ userInputState, valve, region }: Props) => {
    const isEffectivePV2Meaningful = valve.calcEffectivePV2 && valve.calcEffectivePV2 !== valve.calcEffectivePV;
    const is6way = valve.pattern?.raw.join('').includes('6');

    const emptyItem: [string | null | undefined, string | null | undefined, string | null | undefined][] = [];
    const calculatables: [string | null | undefined, string | null | undefined, string | null | undefined][] = [
        ...(valve.calcEffectiveDpv ? [
            createItem(MESSAGES.EffectiveDpv, valve.calcEffectiveDpv),
        ] : emptyItem),
        ...(is6way && valve.calcEffectiveDpv6way1 ? [
            createItem(MESSAGES.EffectiveDpv, valve.calcEffectiveDpv6way1, MESSAGES.seq1),
        ] : emptyItem),
        ...(is6way && valve.calcEffectiveDpv6way2 ? [
            createItem(null, valve.calcEffectiveDpv6way2, MESSAGES.seq2),
        ] : emptyItem),
        ...(valve.calcMinReqDpv ? [
            createItem(
                MESSAGES.MinReqDpv,
                valve.calcMinReqDpv,
                is6way ? MESSAGES.seq1 : null,
            ),
        ] : emptyItem),
        ...(is6way && valve.calcMinReqDpv2 ? [
            createItem(null, valve.calcMinReqDpv2, MESSAGES.seq2),
        ] : emptyItem),
        ...(valve.calcEffectivePV ? [
            createItem(MESSAGES.EffectivePV, valve.calcEffectivePV),
        ] : emptyItem),
        ...(is6way && valve.calcEffectivePV1 && isEffectivePV2Meaningful ? [
            createItem(MESSAGES.EffectivePV, valve.calcEffectivePV1, MESSAGES.seq1),
        ] : emptyItem),
        ...(is6way && valve.calcEffectivePV2 && isEffectivePV2Meaningful ? [
            createItem(null, valve.calcEffectivePV2, MESSAGES.seq2),
        ] : emptyItem),
        ...(valve.calcFlowVelocity ? [
            createItem(
                MESSAGES.FlowVelocity,
                valve.calcFlowVelocity,
                is6way ? MESSAGES.seq1 : null,
            ),
        ] : emptyItem),
        ...(is6way && valve.calcFlowVelocity2 ? [
            createItem(null, valve.calcFlowVelocity2, MESSAGES.seq2),
        ] : emptyItem),
    ];

    const flow: DesignRow[] = [
        {
            key: getUserInputFormFieldKey(region, 'v100'),
            detectNamespace: false,
        },
        ` ${userInputState.v100} `,
        {
            key: userInputState.v100_u,
        },
        ...(userInputState.v100_2.length > 0 ? [
            ' ',
            {
                key: MESSAGES.seq1,
            },
            ',',
            ` ${userInputState.v100_2} `,
            {
                key: userInputState.v100_2_u,
            },
            ' ',
            {
                key: MESSAGES.seq2,
            },
        ] : []),
        ', ',
    ];

    return [
        ...(userInputState.v100.length > 0 ? flow : []),
        ...((calculatables)
            .reduce((acc, [k, v, s], currentIndex) => {
                if (!v) {
                    return acc;
                }
                if (k !== null && k !== undefined) {
                    acc.push({ key: k });
                }
                acc.push(`${k !== null && k !== undefined ? ' ' : ''}${v}${s !== null && s !== undefined ? ' ' : ''}`);
                if (s !== null && s !== undefined) {
                    acc.push({ key: s });
                }
                if (currentIndex !== calculatables.length - 1) {
                    acc.push(', ');
                }
                return acc;
            }, [] as DesignRow[])
        ),
    ];
};
